import { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import * as R from 'ramda';

import UserGroupIcon from '@atom/components/common/UserGroupIcon';
import { TASK_USER_GROUP_REMOVE } from '@atom/graph/task';
import { GET_USERS } from '@atom/graph/user';
import { GET_USER_GROUP } from '@atom/graph/userGroup';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Button, Icon, IconButton, Modal } from '@atom/mui';
import { Task, TaskUserGroupRemoveInput } from '@atom/types/task';
import {
  UserDetail,
  UsersConnection,
  UsersConnectionInput,
} from '@atom/types/user';
import { UserGroup } from '@atom/types/userGroups';
import { WorkOrderDetailType } from '@atom/types/work';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { pluralize } from '@atom/utilities/stringUtilities';

import EditTaskUsers from './EditTaskUsers';

import classes from './editTaskUserGroupModal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  userGroupId: string;
  quantity: number;
  selectedBudgetId: string;
  workTime: number;
  task: Task;
  workOrderDetail: WorkOrderDetailType;
  refetch: () => void;
}

const PAGE_LIMIT = 25;

const EditTaskUserGroupModal = (props: Props) => {
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [, { Link }] = useNavigation();

  const [getUserGroup, { data, error, loading }] = useLazyQuery<{
    userGroup: UserGroup;
  }>(GET_USER_GROUP, {
    fetchPolicy: 'no-cache',
  });

  const [getUsers, { data: usersData, loading: usersLoading }] = useLazyQuery<
    { users: UsersConnection },
    { input: UsersConnectionInput }
  >(GET_USERS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    const nextUsers = R.pathOr([], ['users', 'users'], usersData);

    setUsers([...users, ...nextUsers]);
    setTotal(usersData?.users?.totalCount || 0);
  }, [usersData]);

  const [taskUserGroupRemove] = useMutation<
    { taskUserGroupRemove: Task },
    { input: TaskUserGroupRemoveInput }
  >(TASK_USER_GROUP_REMOVE, {
    onCompleted: () => {
      props.refetch();
    },
  });

  useEffect(() => {
    if (props.open) {
      getUserGroup({
        variables: {
          id: props.userGroupId,
          input: {
            includeRestoredBudgets:
              props.workOrderDetail.reopened && !props.workOrderDetail.isClosed,
          },
        },
      });
    }

    if (props.open && R.isEmpty(users) && !page) {
      setPage(1);
    }

    if (!props.open) {
      setUsers([]);
      setPage(0);
    }
  }, [props.open, getUserGroup]);

  useEffect(() => {
    if (page) {
      getUsers({
        variables: {
          input: {
            showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
            userGroupId: props.userGroupId,
            limit: PAGE_LIMIT,
            page,
          },
        },
      });
    }
  }, [page]);

  const removeUserGroup = async () => {
    await taskUserGroupRemove({
      variables: {
        input: {
          id: props.task.id,
          userGroupId: props.userGroupId,
          workOrderId: props.workOrderDetail.id,
        },
      },
    });

    props.refetch();
    props.onClose();
  };

  const userGroup = data?.userGroup;
  const isLoading = loading || error || R.isNil(userGroup);
  const fullGroupPath = [
    'Directory',
    ...R.pathOr([], ['groupPath'], userGroup),
  ];

  const pathIds = userGroup
    ? [...userGroup.ancestors.map(({ id }) => id), props.userGroupId]
    : [];

  const modalTitle = (
    <div className={classes.headerContainer}>
      <div className={classes.headerLeft}>
        {isLoading ? (
          <div />
        ) : (
          <>
            <div>
              <UserGroupIcon colorId={userGroup.colorId} />
            </div>
            <div className={classes.titleContainer}>
              <div className={classes.headerTitle}>{userGroup.name}</div>
              <div className={classes.headerPath}>
                {total} {pluralize(total, 'User')}
                <span className={classes.spacer}>•</span>
                {fullGroupPath.join(' / ')}
              </div>
            </div>
          </>
        )}
      </div>
      {!isLoading && (
        <Link
          to={`/team?activeGroupId=${props.userGroupId}&activePathIds=${pathIds}`}
        >
          <IconButton>
            <Icon>launch</Icon>
          </IconButton>
        </Link>
      )}
    </div>
  );

  const handlePageScroll = () => {
    setPage(page + 1);
  };

  const renderFooter = (
    <div className={classes.modalFooter}>
      <div>
        {hasRolePermissions(ROLE_SETS.MANAGER) && (
          <Button onClick={removeUserGroup} disabled={!!isLoading}>
            Remove From Task
          </Button>
        )}
      </div>
      <div>
        <Button onClick={props.onClose}>Cancel</Button>
      </div>
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      open={props.open}
      footer={renderFooter}
      loading={loading}
    >
      <EditTaskUsers
        loading={usersLoading}
        users={users}
        total={total}
        handlePageScroll={handlePageScroll}
      />
    </Modal>
  );
};

export default EditTaskUserGroupModal;
